// System → Affärssystemet → Journal (owner 2026-11-04, mockup admin-v1/erp-journal): the ERP's stock
// journal as Trail reads it, and what Trail did with each entry. Four step cards (backfill, opening
// position, applied, live), the counts, filters, the rows 200 at a time, and a detail per entry with
// the ERP's row and Trail's events. The opening position is a one-time action here (config.edit).

import { useCallback, useEffect, useState } from "react";
import { Admin, type ErpJournalEntry, type ErpJournalState } from "../api/client.js";
import { t } from "../i18n.js";
import { DetailBar, useDetailHistory } from "./settings-ui.jsx";

const TYPES = ["sale", "purchase", "loss", "transfer", "consume", "produce", "hire", "loan", "opening", "other"] as const;
const STATES = ["pending", "matched", "applied", "ignored"] as const;
type Period = "30" | "365" | "all";
const fmtDt = (iso: string) => new Date(iso).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" });
const fmtN = (n: number | string) => Number(n).toLocaleString("sv-SE", { maximumFractionDigits: 3 });
const typeLabel = (k: string) => t(`adm.journal.type.${TYPES.includes(k as typeof TYPES[number]) ? k : "other"}` as "adm.journal.type.sale");
const stateLabel = (s: string) => t(`adm.journal.st.${STATES.includes(s as typeof STATES[number]) ? s : "pending"}` as "adm.journal.st.pending");
const fromDate = (p: Period): string | undefined => (p === "all" ? undefined : new Date(Date.now() - Number(p) * 86400_000).toISOString().slice(0, 10));

/** Colour + word for the Trail column: matched/applied green, applied with a note (a guess, a shortage) amber,
 *  pending by Trail's own user red (a report went astray), the rest grey. */
function pillOf(e: ErpJournalEntry): { cls: string; what: string | null; whatCls: string } {
  if (e.state === "matched") return { cls: "ok", what: e.note, whatCls: "" };
  if (e.state === "applied" && e.note?.startsWith("Trails användare")) return { cls: "warn", what: e.note, whatCls: "bad" };
  if (e.state === "applied") return { cls: e.note && !e.note.startsWith("applied with") && !e.note.startsWith("reversed") && !e.note.startsWith("producerat") && !e.note.startsWith("transfer") ? "warn" : "ok", what: e.note, whatCls: e.note ? "dev" : "" };
  // A row by an ERP user Trail has written as, that no report explains: a report astray, for someone to look at.
  if (e.note?.startsWith("Trails användare")) return { cls: e.state === "applied" ? "warn" : "bad", what: e.note, whatCls: "bad" };
  if (e.state === "ignored") return { cls: "mut", what: e.note, whatCls: "" };
  return { cls: "mut", what: e.note, whatCls: "" };
}
const partyLine = (e: ErpJournalEntry) => {
  if (e.type === "transfer") return e.stockChange.startsWith("-") ? t("adm.journal.toWarehouse", { w: e.otherWarehouse ?? "?" }) : t("adm.journal.fromWarehouse", { w: e.otherWarehouse ?? "?" });
  if (!e.partyKey) return "";
  return e.type === "purchase" ? t("adm.journal.supplier", { key: e.partyKey }) : t("adm.journal.customer", { key: e.partyKey });
};

export function ErpJournal() {
  const [st, setSt] = useState<ErpJournalState | null>(null);
  const [state, setState] = useState<string>("all");
  const [type, setType] = useState<string>("all");
  const [sku, setSku] = useState("");
  const [order, setOrder] = useState("");
  const [period, setPeriod] = useState<Period>("30");
  const [limit, setLimit] = useState(200);
  const [rows, setRows] = useState<ErpJournalEntry[]>([]);
  const [open, setOpen] = useState<ErpJournalEntry | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useDetailHistory(!!open, () => setOpen(null));

  const load = useCallback(async () => {
    setErr(null);
    try {
      const [s, r] = await Promise.all([
        Admin.erpJournal(),
        Admin.erpJournalEntries({ ...(state !== "all" ? { state } : {}), ...(type !== "all" ? { type } : {}), sku: sku.trim(), order: order.trim(), ...(fromDate(period) ? { from: fromDate(period)! } : {}), limit }),
      ]);
      setSt(s);
      setRows(r.items);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }, [state, type, sku, order, period, limit]);
  useEffect(() => { const h = setTimeout(() => void load(), sku || order ? 250 : 0); return () => clearTimeout(h); }, [load, sku, order]);
  // The replay moves while the page is open: refresh the counts every half minute.
  useEffect(() => { const h = setInterval(() => { if (!open) void load(); }, 30_000); return () => clearInterval(h); }, [load, open]);

  const opening = async () => {
    setBusy(true); setMsg(null); setErr(null);
    try { const r = await Admin.erpJournalOpening(); setMsg(t("adm.journal.opening.done", { openings: fmtN(r.openings), negative: fmtN(r.negative) })); await load(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  const by = st?.byState ?? {};
  const total = Object.values(by).reduce((a, b) => a + b, 0) - (by["ignored"] ?? 0) * 0;
  const openingRows = st?.openingReport?.openings ?? null;
  const pendingAll = st?.pending ?? 0;
  const backfillDone = !!st?.backfillDoneAt;
  const openingDone = !!st?.openingDoneAt;

  return (
    <>
      {err && <p className="ahint" style={{ color: "var(--bad)" }}>{err}</p>}
      {msg && <p className="ahint" style={{ color: "var(--ok)" }}>{msg}</p>}
      {open ? (
        <EntryDetail e={open} onBack={() => setOpen(null)} />
      ) : (
        <>
          <div className="steps4">
            <div className={`tile${backfillDone ? " done" : " now"}`}>
              <div className="k">{t("adm.journal.step.backfill")}</div>
              <div className="v">{fmtN(total)}</div>
              <div className="d">{backfillDone ? t("adm.journal.step.backfill.d", { floor: st?.floor ?? "—" }) : t("adm.journal.step.backfill.pending")}</div>
            </div>
            <div className={`tile${openingDone ? " done" : backfillDone ? " now" : ""}`}>
              <div className="k">{t("adm.journal.step.opening")}</div>
              <div className="v">{openingDone && openingRows !== null ? fmtN(openingRows) : "—"}</div>
              <div className="d">{openingDone ? t("adm.journal.step.opening.d", { negative: fmtN(st?.openingReport?.negative ?? 0) }) : backfillDone ? t("adm.journal.step.opening.none") : t("adm.journal.step.opening.wait")}</div>
            </div>
            <div className={`tile${openingDone ? (pendingAll > 0 ? " now" : " done") : ""}`}>
              <div className="k">{t("adm.journal.step.applied")}</div>
              <div className="v">{fmtN(by["applied"] ?? 0)}</div>
              <div className="d">{!openingDone ? t("adm.journal.step.applied.wait") : pendingAll > 0 ? t("adm.journal.step.applied.d", { pending: fmtN(pendingAll) }) : t("adm.journal.step.applied.done")}</div>
            </div>
            <div className="tile">
              <div className="k">{t("adm.journal.step.live")}</div>
              <div className="v">{fmtN(pendingAll)}</div>
              <div className="d">{st?.lastAppliedAt ? t("adm.journal.step.live.d", { at: fmtDt(st.lastAppliedAt) }) : t("adm.journal.step.live.none")}</div>
            </div>
          </div>
          {backfillDone && !openingDone && (
            <div className="card"><div className="bd" style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
              <button type="button" className="abtn primary" disabled={busy} onClick={() => void opening()}>{t("adm.journal.opening.do")}</button>
              <span className="ahint" style={{ fontSize: 13 }}>{t("adm.journal.opening.hint")}</span>
            </div></div>
          )}
          <div className="card">
            <div className="jcounts">
              <div><b>{fmtN(total)}</b>{t("adm.journal.count.all")}</div>
              <div><b>{fmtN(by["matched"] ?? 0)}</b>{t("adm.journal.count.matched")}</div>
              <div><b>{fmtN(by["applied"] ?? 0)}</b>{t("adm.journal.count.applied")}</div>
              <div className={pendingAll ? "warn" : ""}><b>{fmtN(pendingAll)}</b>{t("adm.journal.count.pending")}</div>
              <div><b>{fmtN(by["ignored"] ?? 0)}</b>{t("adm.journal.count.ignored")}</div>
            </div>
            <div className="bd" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", borderBottom: "1px solid var(--line)" }}>
              <div className="achips">
                <button type="button" className={`achip${state === "all" ? " on" : ""}`} onClick={() => setState("all")}>{t("adm.journal.st.all")}</button>
                {STATES.map((s) => <button key={s} type="button" className={`achip${state === s ? " on" : ""}`} onClick={() => setState(s)}>{stateLabel(s)}</button>)}
              </div>
              <select value={type} onChange={(e) => setType(e.target.value)}>
                <option value="all">{t("adm.journal.type.all")}</option>
                {TYPES.map((k) => <option key={k} value={k}>{typeLabel(k)}</option>)}
              </select>
              <input className="ain" style={{ height: 32, width: 150 }} value={sku} onChange={(e) => setSku(e.target.value)} placeholder={t("adm.journal.filter.sku")} />
              <input className="ain" style={{ height: 32, width: 120 }} value={order} onChange={(e) => setOrder(e.target.value)} placeholder={t("adm.journal.filter.order")} />
              <select value={period} onChange={(e) => setPeriod(e.target.value as Period)}>
                <option value="30">{t("adm.journal.period.30")}</option>
                <option value="365">{t("adm.journal.period.365")}</option>
                <option value="all">{t("adm.journal.period.all")}</option>
              </select>
            </div>
            <div className="bd flush">
              <table className="at">
                <thead><tr><th>{t("adm.journal.col.date")}</th><th>{t("adm.journal.col.type")}</th><th>{t("adm.journal.col.sku")}</th><th>{t("adm.journal.col.batch")}</th><th className="num">{t("adm.journal.col.change")}</th><th>{t("adm.journal.col.order")}</th><th>{t("adm.journal.col.by")}</th><th>{t("adm.journal.col.trail")}</th></tr></thead>
                <tbody>
                  {rows.length === 0 && <tr><td colSpan={8} className="mut">{t("adm.journal.none")}</td></tr>}
                  {rows.map((e) => {
                    const p = pillOf(e);
                    return (
                      <tr key={e.key} className="click" onClick={() => setOpen(e)}>
                        <td className="id">{e.at}</td>
                        <td>{typeLabel(e.type)}{e.corrected && <div className="what">{t("adm.journal.corrected")}</div>}</td>
                        <td>{e.sku}</td>
                        <td className="id">{e.lot ?? <span className="mut">—</span>}{e.rowKey ? <span className="mut"> · {e.rowKey}</span> : null}</td>
                        <td className="num">{(Number(e.stockChange) > 0 ? "+" : "") + fmtN(e.stockChange)} {e.uom}</td>
                        <td className="id wrap">{e.orderRef ?? ""}{partyLine(e) ? <span className="mut"> · {partyLine(e)}</span> : null}</td>
                        <td className="id">{e.by ?? ""}</td>
                        <td><span className={`pill ${p.cls}`}>{stateLabel(e.state)}</span>{p.what && <div className={`what ${p.whatCls}`}>{p.what}</div>}</td>
                      </tr>
                    );
                  })}
                  {rows.length >= limit && <tr><td colSpan={8} style={{ textAlign: "center" }}><button type="button" className="abtn sm" onClick={() => setLimit((l) => Math.min(500, l + 200))}>{t("adm.journal.more")}</button></td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function EntryDetail({ e, onBack }: { e: ErpJournalEntry; onBack: () => void }) {
  const p = pillOf(e);
  const ext = ((e as unknown as { external?: Record<string, Record<string, unknown>> }).external ?? {});
  const raw = Object.values(ext)[0] ?? null;
  return (
    <div className="card">
      <DetailBar title={`${typeLabel(e.type)} · ${e.sku} · ${(Number(e.stockChange) > 0 ? "+" : "") + fmtN(e.stockChange)} ${e.uom}`} sub={<span className={`pill ${p.cls}`}>{stateLabel(e.state)}</span>} onBack={onBack} />
      <div className="bd">
        <dl className="kv">
          <dt>{t("adm.journal.detail.erp")}</dt><dd>{t("adm.journal.detail.erpLine", { key: e.key, date: e.at, by: e.by ?? "—" })}{e.corrected ? ` · ${t("adm.journal.corrected")}` : ""}</dd>
          <dt>{t("adm.journal.detail.order")}</dt><dd className="id">{e.orderRef ? `${e.orderRef}${e.orderKind ? ` · ${e.orderKind}` : ""}` : "—"}{partyLine(e) ? ` · ${partyLine(e)}` : ""}{e.invoice ? ` · ${e.invoice}` : ""}</dd>
          <dt>{t("adm.journal.detail.row")}</dt><dd className="id">{t("adm.journal.detail.rowLine", { row: e.rowKey ?? "—", lot: e.lot ?? t("adm.journal.detail.noLot"), w: e.warehouse ?? "—" })}{e.otherWarehouse ? ` · ${partyLine(e)}` : ""}</dd>
          <dt>{t("adm.journal.detail.qty")}</dt><dd>{(Number(e.stockChange) > 0 ? "+" : "") + fmtN(e.stockChange)} {e.uom}{raw && typeof raw === "object" && "quantity" in raw ? ` · ${String((raw as { quantity?: unknown }).quantity)}` : ""}</dd>
          <dt>{t("adm.journal.detail.trail")}</dt><dd>{e.note ?? (e.state === "applied" ? stateLabel(e.state) : t("adm.journal.detail.nothing"))}</dd>
          {e.matchedReportId && <><dt>{t("adm.journal.detail.report")}</dt><dd className="id">{e.matchedReportId}</dd></>}
          {e.applied && e.applied.events.length > 0 && <><dt>{t("adm.journal.detail.events")}</dt><dd className="id">{e.applied.events.map((x) => x.type).join(" · ")}</dd></>}
          {e.applied && e.applied.containers.length > 0 && <><dt>{t("adm.journal.detail.containers")}</dt><dd className="id wrap">{e.applied.containers.map((c) => c.slice(-8)).join(", ")}</dd></>}
        </dl>
      </div>
    </div>
  );
}
